import { Injectable, NgZone } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { environment } from '../../environments/environment';

/**
 * VoiceService — voice IN as a LIVE stream (mic → AudioWorklet → PCM →
 * backend WS → Deepgram → interim+final transcripts back to the popup),
 * voice OUT via the browser's `speechSynthesis`.
 *
 * Live recording flow:
 *   1. component calls startListening({userName, userType}) → opens WS to
 *      /aiChat/transcribe-live, grabs the mic, spins up an AudioWorklet
 *   2. PCM frames stream to backend → Deepgram → interim text comes back
 *   3. transcript$ updates ~every 200–500 ms with growing text
 *   4. component calls stopRecording() when user clicks Send → returns the
 *      final accumulated transcript and closes the WS
 *   5. component fills composer + auto-sends, then calls hidePopup()
 */
@Injectable({ providedIn: 'root' })
export class VoiceService {

  // ── State observables ──────────────────────────────────────────
  private readonly _isListening$    = new BehaviorSubject<boolean>(false);
  private readonly _isSpeaking$     = new BehaviorSubject<boolean>(false);
  private readonly _isTranscribing$ = new BehaviorSubject<boolean>(false);
  private readonly _transcript$     = new BehaviorSubject<string>('');

  readonly isListening$:    Observable<boolean> = this._isListening$.asObservable();
  readonly isSpeaking$:     Observable<boolean> = this._isSpeaking$.asObservable();
  readonly isTranscribing$: Observable<boolean> = this._isTranscribing$.asObservable();
  readonly transcript$:     Observable<string>  = this._transcript$.asObservable();

  readonly supported: { recognition: boolean; synthesis: boolean };

  // ── Internals ──────────────────────────────────────────────────
  private currentUtterance: SpeechSynthesisUtterance | null = null;
  private voicesCache: SpeechSynthesisVoice[] = [];

  // Live STT plumbing
  private mediaStream: MediaStream | null = null;
  private audioCtx: AudioContext | null = null;
  private workletNode: AudioWorkletNode | null = null;
  private liveWs: WebSocket | null = null;
  private finalText = '';   // accumulated final segments
  private interimText = ''; // latest interim segment (replaced as it grows)

  // Legacy / fallback (kept so the /aiChat/transcribe REST endpoint can still
  // be used if Deepgram is unavailable — not wired today).
  private recorder: MediaRecorder | null = null;
  private chunks: BlobPart[] = [];

  constructor(private zone: NgZone) {
    const w = window as any;
    const hasRecorder = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)
                     && typeof (w.MediaRecorder) !== 'undefined';
    const hasSynth = typeof w.speechSynthesis !== 'undefined'
                  && typeof w.SpeechSynthesisUtterance !== 'undefined';

    this.supported = {
      recognition: hasRecorder,
      synthesis:   hasSynth
    };

    if (this.supported.synthesis) {
      // Voices populate asynchronously — cache when they arrive.
      const loadVoices = () => { this.voicesCache = w.speechSynthesis.getVoices() || []; };
      loadVoices();
      try { w.speechSynthesis.onvoiceschanged = loadVoices; } catch {}
    }
  }

  // ── Live recording (Deepgram via backend WS proxy) ─────────────
  /** Open the mic + a WebSocket to /aiChat/transcribe-live, start streaming
   *  PCM, push interim transcripts to transcript$. The popup becomes visible
   *  via the isListening$ subscription on the component.
   *
   *  language values:
   *    "multi" (default) — code-switch between EN, HI, ES, FR, DE, PT, JA, IT, NL, RU
   *    "en", "hi", "es", "fr", "de", "pt", "ja", "it", "nl", "ru" — single (nova-3)
   *    "ar" — Arabic (auto-routes to nova-2; nova-3 doesn't support it yet)
   *    Malayalam is NOT supported live — caller should switch to batch /transcribe.
   */
  async startListening(authParams: { userName: string; userType: string; language?: string }): Promise<void> {
    if (!this.supported.recognition) return;
    if (this._isListening$.value || this._isTranscribing$.value) return;

    this.finalText = '';
    this.interimText = '';

    // 1. Mic permission + stream. Skip virtual audio cables (VB-Audio,
    //    VoiceMeeter, etc.) — they're loopback devices with no real input.
    let stream: MediaStream;
    try {
      const audioConstraints: MediaTrackConstraints = {
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true
      };
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const VIRTUAL_RX = /(cable|virtual|voicemeeter|vb-audio|loopback)/i;
        const real = devices.find(d => d.kind === 'audioinput' && d.label && !VIRTUAL_RX.test(d.label));
        if (real?.deviceId) {
          audioConstraints.deviceId = { exact: real.deviceId };
          // eslint-disable-next-line no-console
          console.log('[Voice] selected real mic:', real.label);
        }
      } catch { /* ignore device-enum errors and let browser pick */ }

      stream = await navigator.mediaDevices.getUserMedia({ audio: audioConstraints });
    } catch (e: any) {
      const name = e?.name || 'error';
      this.zone.run(() => {
        if (name === 'NotAllowedError' || name === 'SecurityError') {
          this._transcript$.next('(Microphone access blocked — allow it in the browser address bar and try again.)');
        } else if (name === 'NotFoundError') {
          this._transcript$.next('(No microphone detected.)');
        } else {
          this._transcript$.next('(Could not open the microphone.)');
        }
        this._isListening$.next(true);
        setTimeout(() => this.zone.run(() => this._isListening$.next(false)), 2200);
      });
      return;
    }
    this.mediaStream = stream;

    // 2. AudioContext + PCM worklet. We let the context use the device's
    //    native rate (usually 48000 in Chrome) — forcing a lower rate makes
    //    Chrome silently produce all-zero buffers in some setups.
    try {
      this.audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
      // eslint-disable-next-line no-console
      console.log('[Voice] AudioContext sampleRate=', this.audioCtx.sampleRate);
      // Use the served static path. Both ng-serve and prod expose /assets/.
            const workletCode = `
        class PcmProcessor extends AudioWorkletProcessor {
          process(inputs) {
            const ch = inputs[0] && inputs[0][0];
            if (ch) {
              const pcm = new Int16Array(ch.length);
              for (let i = 0; i < ch.length; i++) {
                const s = Math.max(-1, Math.min(1, ch[i]));
                pcm[i] = s < 0 ? s * 0x8000 : s * 0x7fff;
              }
              this.port.postMessage(pcm.buffer, [pcm.buffer]);
            }
            return true;
          }
        }
        registerProcessor('pcm-processor', PcmProcessor);
      `;
      const workletUrl = URL.createObjectURL(new Blob([workletCode], { type: 'application/javascript' }));
      await this.audioCtx.audioWorklet.addModule(workletUrl);
      URL.revokeObjectURL(workletUrl);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Voice] AudioWorklet setup failed', e, 'secure=', window.isSecureContext, 'origin=', location.origin);
      this.zone.run(() => {
        this._transcript$.next('(Audio engine unavailable in this browser.)');
        this._isListening$.next(true);
        setTimeout(() => this._cleanup(), 1800);
      });
      return;
    }

    // 3. Open the WS to backend. Pass the actual AudioContext rate + the
    //    chosen language so the backend can configure Deepgram correctly.
    const language = (authParams as any).language || 'multi';
    const wsUrl = (environment.baseUrl.replace(/^http/, 'ws')
                + 'aiChat/transcribe-live')
                + '?user_name=' + encodeURIComponent(authParams.userName)
                + '&user_type=' + encodeURIComponent(authParams.userType)
                + '&sample_rate=' + (this.audioCtx?.sampleRate || 48000)
                + '&language=' + encodeURIComponent(language);
    let ws: WebSocket;
    try { ws = new WebSocket(wsUrl); }
    catch (e) {
      // eslint-disable-next-line no-console
      console.error('[Voice] WS construct failed', e);
      this.zone.run(() => {
        this._transcript$.next('(Live transcription unavailable. Check your connection.)');
        this._isListening$.next(true);
        setTimeout(() => this._cleanup(), 1800);
      });
      return;
    }
    ws.binaryType = 'arraybuffer';
    this.liveWs = ws;

    ws.onopen = () => {
      // Wire mic → worklet → ws (only after ws is open, otherwise audio is lost)
      try {
        const source = this.audioCtx!.createMediaStreamSource(stream);
        const node   = new AudioWorkletNode(this.audioCtx!, 'pcm-processor');
        node.port.onmessage = (e: MessageEvent) => {
          // ArrayBuffer of Int16 PCM
          if (ws.readyState === WebSocket.OPEN) {
            try { ws.send(e.data); } catch { /* sender gone */ }
          }
        };
        source.connect(node);
        // Don't connect node → destination (we don't want to play the user back)
        this.workletNode = node;
      } catch (e) {
        console.error('[Voice] worklet wire-up failed', e);
      }
    };

    ws.onmessage = (e) => {
      let msg: any;
      try { msg = JSON.parse(typeof e.data === 'string' ? e.data : new TextDecoder().decode(e.data)); }
      catch { return; }
      // Log every relayed Deepgram packet so we can see what's coming back.
      // eslint-disable-next-line no-console
      console.log('[Voice WS]', msg.type, msg.text !== undefined ? ('"' + msg.text + '"') : '', msg.is_final !== undefined ? ('final=' + msg.is_final) : '');
      if (msg.type === 'transcript') {
        if (msg.is_final) {
          this.finalText = (this.finalText + ' ' + (msg.text || '')).trim();
          this.interimText = '';
        } else {
          this.interimText = msg.text || '';
        }
        const live = (this.finalText + (this.interimText ? ' ' + this.interimText : '')).trim();
        this.zone.run(() => this._transcript$.next(live));
      } else if (msg.type === 'error') {
        this.zone.run(() => this._transcript$.next('(' + (msg.message || 'Live STT error.') + ')'));
      }
    };

    ws.onerror = () => {
      // eslint-disable-next-line no-console
      console.warn('[Voice] WS error');
    };

    ws.onclose = () => {
      // If we still think we're listening, the upstream went away — close popup gracefully.
      if (this._isListening$.value) {
        setTimeout(() => this.zone.run(() => this._isListening$.next(false)), 100);
      }
    };

    this.zone.run(() => {
      this._transcript$.next('');
      this._isListening$.next(true);
    });
  }

  /** Stop streaming. Returns the final accumulated transcript text. */
  async stopRecording(): Promise<string> {
    // Send a graceful close to flush Deepgram's last segment.
    if (this.liveWs && this.liveWs.readyState === WebSocket.OPEN) {
      try { this.liveWs.send(JSON.stringify({ action: 'stop' })); } catch {}
    }
    // Give Deepgram ~250 ms to deliver the last is_final.
    await new Promise(r => setTimeout(r, 250));
    const text = (this.finalText + (this.interimText ? ' ' + this.interimText : '')).trim();
    this._cleanup();
    return text;
  }

  /** Abort recording without committing the transcript — Cancel button. */
  stopListening(): void {
    this._cleanup();
    this.zone.run(() => {
      this._isListening$.next(false);
      this._isTranscribing$.next(false);
      this._transcript$.next('');
    });
  }

  private _cleanup(): void {
    try { this.workletNode?.disconnect(); } catch {}
    try { this.audioCtx?.close(); } catch {}
    try { this.mediaStream?.getTracks().forEach(t => t.stop()); } catch {}
    try { if (this.liveWs && this.liveWs.readyState <= 1) this.liveWs.close(); } catch {}
    this.workletNode = null;
    this.audioCtx = null;
    this.mediaStream = null;
    this.liveWs = null;
  }

  /** Component drives popup state during the upload/transcribe phase. */
  setTranscribingState(active: boolean): void {
    this.zone.run(() => this._isTranscribing$.next(active));
  }

  /** Component writes the final transcript into the popup transcript area. */
  setTranscript(text: string): void {
    this.zone.run(() => this._transcript$.next(text || ''));
  }

  /** Close the popup. Used after transcription completes (success or fail). */
  hidePopup(): void {
    this.zone.run(() => {
      this._isListening$.next(false);
      this._isTranscribing$.next(false);
    });
  }

  // ── Synthesis ──────────────────────────────────────────────────
  speak(text: string, opts?: { rate?: number; pitch?: number; onEnd?: () => void }): void {
    if (!this.supported.synthesis) return;
    const raw = (text || '').trim();
    if (!raw) return;

    // One voice at a time — stop anything already playing first.
    this.stopSpeaking();

    // Speak in the SAME language the reply is written in: detect the script,
    // pick a matching installed voice, and tag the utterance's language so the
    // engine uses the right pronunciation (Arabic, Hindi, Malayalam, …).
    const lang = this.detectLang(raw);

    // Some browsers (esp. Chrome) trim speech when the utterance exceeds
    // ~200 chars or contains long table-style content. We chunk on sentence
    // boundaries and queue them so the whole reply gets spoken naturally.
    const humanized = this.humanizeForSpeech(raw, lang);
    const chunks = this.chunkForSpeech(humanized);
    if (!chunks.length) return;

    const voice = this.pickBestVoice(lang);
    const fallbackLang = this.bcp47(lang);
    const rate  = opts?.rate  ?? 1.05;  // slight bump = less monotone
    const pitch = opts?.pitch ?? 1.0;

    let chunkIdx = 0;
    const speakNext = () => {
      if (chunkIdx >= chunks.length) {
        this.zone.run(() => this._isSpeaking$.next(false));
        this.currentUtterance = null;
        opts?.onEnd && opts.onEnd();
        return;
      }
      const u = new SpeechSynthesisUtterance(chunks[chunkIdx++]);
      u.rate  = rate;
      u.pitch = pitch;
      u.lang  = voice?.lang || fallbackLang;
      if (voice) u.voice = voice;

      u.onstart = () => this.zone.run(() => this._isSpeaking$.next(true));
      u.onend   = () => speakNext();
      u.onerror = () => speakNext();  // skip broken chunk, keep going

      this.currentUtterance = u;
      try { window.speechSynthesis.speak(u); }
      catch { this._isSpeaking$.next(false); }
    };
    speakNext();
  }

  /**
   * Pick the most natural-sounding voice available for the given language.
   * For non-English we match by the 2-letter prefix (e.g. 'ar', 'hi', 'ml');
   * if no voice for that language is installed we return null so the caller
   * falls back to tagging the utterance's `lang` and letting the engine try.
   */
  private pickBestVoice(lang: string = 'en'): SpeechSynthesisVoice | null {
    const voices = this.voicesCache.length
      ? this.voicesCache
      : (window.speechSynthesis.getVoices() || []);
    if (!voices.length) return null;

    if (lang !== 'en') {
      const matches = voices.filter(v => (v.lang || '').toLowerCase().startsWith(lang));
      if (!matches.length) return null;   // none installed → let u.lang drive a best-effort engine
      matches.sort((a, b) => this.voiceQuality(b) - this.voiceQuality(a));
      return matches[0];
    }

    // English: rich quality scoring across the common Windows/macOS/Chrome voices.
    const en = voices.filter(v => (v.lang || '').toLowerCase().startsWith('en'));
    if (!en.length) return voices[0];
    en.sort((a, b) => this.voiceQuality(b) - this.voiceQuality(a));
    return en[0];
  }

  /** Score a voice by quality cues in its name (language-neutral). Higher = better. */
  private voiceQuality(v: SpeechSynthesisVoice): number {
    const n = (v.name || '').toLowerCase();
    let s = 0;
    if (n.includes('natural')) s += 100;   // Edge "Natural" voices — top tier
    if (n.includes('online'))  s += 60;    // cloud SAPI Web voices
    if (n.includes('neural'))  s += 80;
    if (n.includes('wavenet')) s += 70;
    if (n.includes('premium')) s += 50;
    if (n.includes('enhanced')) s += 40;
    const goodNames = [
      'aria', 'jenny', 'sonia', 'libby',           // Win 11 Edge female
      'guy', 'ryan', 'davis', 'tony',              // Win 11 Edge male
      'samantha', 'allison', 'ava',                // macOS female
      'daniel', 'tom',                             // macOS male
      'karen',                                     // AU/NZ
      'google us english', 'google uk english female'  // Chrome bundled
    ];
    for (const g of goodNames) if (n.includes(g)) { s += 30; break; }
    // Penalise the basic robotic desktop voices.
    if (n.includes('david desktop') || n.includes('zira desktop') ||
        n.includes('mark desktop') || n === 'microsoft david' ||
        n === 'microsoft zira' || n === 'microsoft mark' || n === 'fred') s -= 50;
    if (n.includes('desktop')) s -= 20;    // generic de-prefer (any language)
    if (n.includes('female') || /\b(aria|jenny|sonia|libby|samantha|allison|ava|karen|zira)\b/.test(n)) s += 5;
    if ((v.lang || '').toLowerCase() === 'en-us') s += 3;
    return s;
  }

  /** Rough language detection from the script used — enough to pick a TTS voice. */
  private detectLang(text: string): string {
    const t = text || '';
    // Urdu uses Arabic script plus a few extra letters — check before generic Arabic.
    if (/[ٹڈڑںھہے]/.test(t)) return 'ur';
    if (/[؀-ۿݐ-ݿ]/.test(t)) return 'ar';   // Arabic
    if (/[ऀ-ॿ]/.test(t)) return 'hi';                // Devanagari (Hindi)
    if (/[ഀ-ൿ]/.test(t)) return 'ml';                // Malayalam
    if (/[஀-௿]/.test(t)) return 'ta';                // Tamil
    if (/[ఀ-౿]/.test(t)) return 'te';                // Telugu
    if (/[ಀ-೿]/.test(t)) return 'kn';                // Kannada
    if (/[ঀ-৿]/.test(t)) return 'bn';                // Bengali
    if (/[਀-੿]/.test(t)) return 'pa';                // Gurmukhi (Punjabi)
    if (/[฀-๿]/.test(t)) return 'th';                // Thai
    if (/[぀-ヿ]/.test(t)) return 'ja';                // Japanese kana
    if (/[가-힯]/.test(t)) return 'ko';                // Korean
    if (/[一-鿿]/.test(t)) return 'zh';                // Chinese (kana caught above)
    if (/[Ѐ-ӿ]/.test(t)) return 'ru';                // Cyrillic
    if (/[Ͱ-Ͽ]/.test(t)) return 'el';                // Greek
    return 'en';                                               // Latin script → English (default)
  }

  /** Map a 2-letter language to a BCP-47 tag the speech engine recognises. */
  private bcp47(lang: string): string {
    const m: { [k: string]: string } = {
      en: 'en-US', ar: 'ar-SA', ur: 'ur-PK', hi: 'hi-IN', ml: 'ml-IN', ta: 'ta-IN',
      te: 'te-IN', kn: 'kn-IN', bn: 'bn-IN', pa: 'pa-IN', th: 'th-TH', ja: 'ja-JP',
      ko: 'ko-KR', zh: 'zh-CN', ru: 'ru-RU', el: 'el-GR'
    };
    return m[lang] || 'en-US';
  }

  /** Pre-process the assistant's reply to sound more natural when spoken. */
  private humanizeForSpeech(text: string, lang: string = 'en'): string {
    let s = text;
    if (lang === 'en') {
      // AED → dirhams (move the unit after the number so it sounds natural).
      // Only for English — in other languages the model already wrote the
      // currency in-language, so leave it alone.
      s = s.replace(/\bAED\s+([\d.,]+)/g, '$1 dirhams');
      s = s.replace(/\bAED\b/g, 'dirhams');
    }
    // Strip thousands-separator commas inside numbers so TTS reads the whole
    // number, not "one comma three hundred twenty-seven". (Universal — replies
    // keep Western digits "1,234.56" where comma = thousands separator.)
    s = s.replace(/(\d),(?=\d{3}(?!\d))/g, '$1');
    // Em / en dashes → comma (cleaner pause)
    s = s.replace(/[—–]/g, ', ');
    // Ellipses → comma
    s = s.replace(/\.{3,}/g, ', ');
    // ⚠ prefix the strip-markup may have missed — spoken cue in English, just drop it elsewhere.
    s = s.replace(/⚠️?/g, lang === 'en' ? 'Heads up — ' : ' ');
    // Collapse whitespace introduced by replacements
    s = s.replace(/\s+/g, ' ').trim();
    return s;
  }

  /** Split a long reply into ~140-char sentence-aware chunks for the TTS queue. */
  private chunkForSpeech(text: string): string[] {
    if (text.length <= 180) return [text];
    const chunks: string[] = [];
    const parts = text.split(/(?<=[.!?؟۔।])\s+/);  // split on sentence end (incl. Arabic/Urdu/Hindi)
    let buf = '';
    for (const p of parts) {
      if ((buf + ' ' + p).trim().length > 180 && buf) {
        chunks.push(buf.trim());
        buf = p;
      } else {
        buf = (buf ? buf + ' ' : '') + p;
      }
    }
    if (buf.trim()) chunks.push(buf.trim());
    return chunks;
  }

  stopSpeaking(): void {
    if (!this.supported.synthesis) return;
    try { window.speechSynthesis.cancel(); } catch {}
    this.currentUtterance = null;
    this._isSpeaking$.next(false);
  }

  /**
   * Toggle convenience — if currently speaking, stop and return false;
   * otherwise start speaking the given text and return true.
   */
  toggleSpeak(text: string): boolean {
    if (this._isSpeaking$.value) {
      this.stopSpeaking();
      return false;
    }
    this.speak(text);
    return true;
  }
}
